/**
 * ConfirmDialog.js
 * Dialog de confirmação reutilizável renderizado via React Portal.
 *
 * Props:
 *   isOpen          — boolean, controla visibilidade
 *   title           — string, título do dialog
 *   message         — string | ReactNode, mensagem (opcional)
 *   confirmLabel    — string (default: "Confirmar")
 *   cancelLabel     — string (default: "Cancelar")
 *   variant         — 'danger' | 'warning' | 'info' (default: 'danger')
 *   onConfirm       — callback ao confirmar
 *   onCancel        — callback ao cancelar / fechar
 *   children        — conteúdo extra abaixo da mensagem
 *   confirmDisabled — boolean
 *   loading         — boolean
 *   hideFooter      — boolean — oculta botões padrão (quando children tem seus próprios)
 *   maxWidth        — number | string (opcional) — largura máxima do dialog; sem ela vale a do CSS (400px)
 */

import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './ConfirmDialog.module.css';

// ── Ícones SVG inline ─────────────────────────────────────────────────────────

const IconDanger = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconInfo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const VARIANTS = {
  danger:  { Icon: IconDanger, accent: '#dc2626', bg: '#fef2f2' },
  warning: { Icon: IconDanger, accent: '#d97706', bg: '#fffbeb' },
  info:    { Icon: IconInfo,   accent: '#2563eb', bg: '#eff6ff' },
};

// ─────────────────────────────────────────────────────────────────────────────

export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel    = 'Confirmar',
  cancelLabel     = 'Cancelar',
  variant         = 'danger',
  onConfirm,
  onCancel,
  children,
  confirmDisabled = false,
  loading         = false,
  hideFooter      = false,
  maxWidth,
}) {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onCancel?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const v = VARIANTS[variant] || VARIANTS.danger;
  const { Icon } = v;

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={onCancel} role="dialog" aria-modal="true">
      <div
        className={styles.dialog}
        style={maxWidth ? { maxWidth } : undefined}
        onClick={e => e.stopPropagation()}
      >

        <div className={styles.header} style={{ background: v.bg, borderColor: v.accent + '22' }}>
          <span className={styles.icon} style={{ color: v.accent }}>
            <Icon />
          </span>
          <h3 className={styles.title} style={{ color: v.accent }}>{title}</h3>
        </div>

        <div className={styles.body}>
          {message && <p className={styles.message}>{message}</p>}
          {children}
        </div>

        {!hideFooter && (
          <div className={styles.footer}>
            <button
              type="button"
              className={styles.btnCancel}
              onClick={onCancel}
              disabled={loading}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              className={styles.btnConfirm}
              style={{ background: v.accent }}
              onClick={onConfirm}
              disabled={confirmDisabled || loading}
            >
              {loading && <span className={styles.spinner} />}
              {loading ? 'Aguarde...' : confirmLabel}
            </button>
          </div>
        )}

      </div>
    </div>,
    document.body
  );
}
